import { CostCalculator, costCalculator } from './CostCalculator'
import { FreightCalculator, freightCalculator } from './FreightCalculator'
import { OptimizationEngine, optimizationEngine } from './OptimizationEngine'
import type {
  OptimizationResult,
  OptimizationStrategy,
  OptimizationWeights,
  OptimizerDemandItem,
  PlanComparison,
  SupplierFreightRule,
  SupplierOfferCandidate,
} from '../types'
import { DEFAULT_OPTIMIZATION_WEIGHTS } from '../types'

/**
 * PurchaseOptimizer — orchestrates demand → offers → combinations → best plan.
 */
export class PurchaseOptimizer {
  private engine: OptimizationEngine
  private cost: CostCalculator
  private freight: FreightCalculator

  constructor(
    engine: OptimizationEngine = optimizationEngine,
    cost: CostCalculator = costCalculator,
    freight: FreightCalculator = freightCalculator,
  ) {
    this.engine = engine
    this.cost = cost
    this.freight = freight
  }

  setWeights(weights: Partial<OptimizationWeights>) {
    return this.engine.setWeights(weights)
  }

  getWeights() {
    return this.engine.getWeights()
  }

  setFreightRules(rules: SupplierFreightRule[]) {
    this.freight.setRules(rules)
    this.cost.setFreightRules(rules)
  }

  optimize(input: {
    demands: OptimizerDemandItem[]
    offers: SupplierOfferCandidate[]
    strategy: OptimizationStrategy
    currentFreightEstimate?: number
  }): { result: OptimizationResult; comparison: PlanComparison } {
    const demands = input.demands.filter((d) => d.quantity > 0)
    const currentProductCost = round2(
      demands.reduce((s, d) => s + d.currentUnitPrice * d.quantity, 0),
    )
    const currentFreight = input.currentFreightEstimate ?? estimateBaselineFreight(currentProductCost)
    const baselineTotal = round2(currentProductCost + currentFreight)

    const result = this.engine.optimize(demands, input.offers, input.strategy, baselineTotal)

    const comparison: PlanComparison = {
      current: {
        productCost: currentProductCost,
        freightCost: currentFreight,
        totalCost: baselineTotal,
        supplierCount: estimateCurrentSuppliers(demands),
        estimatedDelivery: 7,
      },
      optimized: {
        productCost: result.productCost,
        freightCost: result.freightCost,
        totalCost: result.totalCost,
        supplierCount: result.supplierCount,
        estimatedDelivery: result.estimatedDelivery,
      },
      economy: result.economy,
      economyPercent: result.economyPercent,
      deliveryDiff: result.estimatedDelivery - 7,
    }

    return { result, comparison }
  }

  /** Evaluate all strategies and return the best by economy (ties → score). */
  optimizeBest(input: {
    demands: OptimizerDemandItem[]
    offers: SupplierOfferCandidate[]
    strategies?: OptimizationStrategy[]
  }) {
    const strategies: OptimizationStrategy[] =
      input.strategies ??
      ([
        'MENOR_PRECO',
        'MENOR_FRETE',
        'MENOR_PRAZO',
        'MENOR_NUMERO_FORNECEDORES',
        'MELHOR_SCORE',
      ] as const)

    let best: { result: OptimizationResult; comparison: PlanComparison } | null = null
    for (const strategy of strategies) {
      const run = this.optimize({ ...input, strategy })
      if (
        !best ||
        run.result.economy > best.result.economy ||
        (run.result.economy === best.result.economy && run.result.score > best.result.score)
      ) {
        best = run
      }
    }
    return best!
  }
}

function estimateBaselineFreight(productCost: number): number {
  if (productCost <= 0) return 0
  if (productCost >= 1500) return 0
  return round2(Math.max(35, productCost * 0.03))
}

function estimateCurrentSuppliers(demands: OptimizerDemandItem[]): number {
  // Draft items often come from mixed sources; approximate as one basket.
  return demands.length > 0 ? Math.min(demands.length, 3) : 0
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const purchaseOptimizer = new PurchaseOptimizer()

export { DEFAULT_OPTIMIZATION_WEIGHTS }
